import {
  handOrder,
  isCityCommand,
  pointCities,
  pointCity,
  releaseUnit,
  setCityMakes,
  trainCityKind,
} from "./cityCommand/cityVerbs";
import { aimCommander, demoteCommander, promoteCommander } from "./court/commanders";
import { clearCall, clearMark, raiseTax, rushCity, setCall, setMark } from "./court/general";
import { commandRefusal, moveKing, sendToFound, stopKing } from "./court/king";
import { appointGovernor, dismissMayor, mayorOf, setStance } from "./court/mayors";
import { type AxialCoord, hexEquals } from "./hex";
import { buildCommand, buildStructure, isImprovement, razeHex } from "./land/build";
import { unitById } from "./run/board";
import { kingOf } from "./run/pools";
import { deploySeat, seatAt, seatUnit } from "./run/seats";
import { enlistWith } from "./soldier/company";
import { standDown, takeCommand } from "./soldier/lead";
import { sellLoad } from "./soldier/market";
import type {
  BuildKind,
  CommandKind,
  CommandRefusal,
  FactionId,
  MayorStance,
  Role,
  RunState,
  SeatId,
  SeatState,
  Unit,
  UnitKind,
} from "./types/state";
import { buildUnit } from "./units/roster";
import { cancelUnitOrder, orderUnit } from "./units/routes";

export { refusalReason } from "./refusalReason";

/**
 * The door every command goes through (ADR-0011 slice 5, ADR-0014 slice M0). A seat is who
 * commands: a faction's King, one of its mayors (by city), one of its commanders or one of
 * its soldiers (by unit). `applyCommand` checks the seat's authority, then the court's (reach
 * and Will, for a King), then calls the rule that exists for the verb (`VERBS`, one entry a
 * verb). The rules are not rewritten: the door is a table in front of them, so a command
 * through it and the direct call give the same bytes (`test/command.test.ts`). The HUD, the
 * debug hook and a network client all produce `Command` values; the court's scripts inside the
 * tick keep calling the rules directly, as the seat's own hands. The city script (CC4) gives its
 * commands as values through this door, whose city-command half is each verb's `city` entry.
 * Why a command would change nothing is `refusalReason` (./refusalReason.ts).
 */
export interface Seat {
  readonly faction: FactionId;
  readonly role: Role;
  /** A mayor's city. */
  readonly city?: AxialCoord;
  /** A commander's or a soldier's own unit. */
  readonly unitId?: number;
}

/** Every verb the game has, as data. The ladder's verbs (M3) are not built. */
export type Command =
  | { readonly kind: "order"; readonly unitId: number; readonly to: AxialCoord }
  | { readonly kind: "stop"; readonly unitId: number }
  | { readonly kind: "train"; readonly city: AxialCoord; readonly unit: UnitKind }
  | { readonly kind: "build"; readonly at: AxialCoord; readonly structure: BuildKind }
  | { readonly kind: "raze"; readonly at: AxialCoord }
  | { readonly kind: "govern"; readonly unitId: number; readonly city: AxialCoord }
  | { readonly kind: "dismiss"; readonly city: AxialCoord }
  | { readonly kind: "promote"; readonly unitId: number }
  | { readonly kind: "demote"; readonly unitId: number }
  | { readonly kind: "aim"; readonly unitId: number; readonly at: AxialCoord }
  | { readonly kind: "stance"; readonly city: AxialCoord; readonly stance: MayorStance }
  | { readonly kind: "mark"; readonly at: AxialCoord; readonly from?: AxialCoord }
  | { readonly kind: "clearMark"; readonly at: AxialCoord }
  | { readonly kind: "call" }
  | { readonly kind: "clearCall" }
  | { readonly kind: "tax" }
  | { readonly kind: "rush"; readonly city: AxialCoord }
  | { readonly kind: "moveKing"; readonly to: AxialCoord }
  | { readonly kind: "stopKing" }
  /** M2: the soldier sells what it carries at the city it stands in or beside. */
  | { readonly kind: "sell"; readonly unitId: number }
  /** C2: the soldier falls in with the commander it stands beside. */
  | { readonly kind: "enlist"; readonly unitId: number; readonly commanderId: number }
  /** VC: the soldier buys Command at its city, and stands down. */
  | { readonly kind: "command"; readonly unitId: number }
  | { readonly kind: "standDown"; readonly unitId: number }
  /** K1: a soldier seat names the own working city its next life rises by, dead or alive. */
  | { readonly kind: "deploy"; readonly city: AxialCoord };

/** Why the door refused: the seat has no say over it, or the court's own reasons. */
export type Refusal = CommandRefusal | "seat";

/**
 * Why a command would change nothing (CC1, `refusalReason`): the door's reasons, then the
 * rule's: no road there, the price, no recruit, no room for a soldier, the yard is training,
 * no site for it, the unit is broken, its order is still locked, a grant is cooling down, or
 * there is nothing left to change.
 */
export type RefusalReason =
  | Refusal
  | "noRoad"
  | "supply"
  | "recruit"
  | "room"
  | "yardBusy"
  | "notSite"
  | "broken"
  | "locked"
  | "cooldown"
  | "unchanged";

/**
 * The seat a seat number names (K0, ADR-0017 point 2), as the door judges it: its kingdom and
 * role, and a soldier seat's living unit. While that unit is dead the seat names no unit and
 * `seatAllows` refuses every command until the respawn. Undefined past the table.
 */
export function seatOf(state: RunState, id: SeatId): Seat | undefined {
  const seat = seatAt(state, id);
  if (!seat) {
    return undefined;
  }
  if (seat.role === "king") {
    return { faction: seat.faction, role: "king" };
  }
  return seat.unitId === undefined
    ? { faction: seat.faction, role: "soldier" }
    : { faction: seat.faction, role: "soldier", unitId: seat.unitId };
}

/**
 * The number of the seat the app plays: the one its screen's copy names (`viewFor`, a room
 * match), else the first seat a human holds (the player's King in a King's run, its soldier in a
 * soldier's, M1; another kingdom's King with `?king=`, B3.3), else the player's King.
 */
export function playerSeatId(state: RunState): SeatId {
  return state.viewer ?? state.seats.find((seat) => seat.held)?.id ?? 0;
}

/** The record of the seat the app plays (`playerSeatId`): its ledger, for the screen. */
export function ownSeat(state: RunState): SeatState | undefined {
  return seatAt(state, playerSeatId(state));
}

/** The living unit of the seat the app plays (M1): a soldier seat's; undefined for a King's and while it is dead. */
export function ownUnit(state: RunState): Unit | undefined {
  return seatUnit(state, ownSeat(state));
}

/** Whether this unit is the soldier of the seat the app plays (M1). */
export function isOwnUnit(state: RunState, unitId: number): boolean {
  return ownSeat(state)?.unitId === unitId;
}

/** Whether the app plays a soldier's seat (M1): the soldier's screen, keys and verbs. */
export function playsSoldier(state: RunState): boolean {
  return playerSeat(state).role === "soldier";
}

/** The seat the app plays (`playerSeatId`) as the door judges it. */
export function playerSeat(state: RunState): Seat {
  return seatOf(state, playerSeatId(state)) ?? { faction: "player", role: "king" };
}

/**
 * The kingdom of the seat the app plays (B3.3): the render and the HUD draw from it, so the
 * human's kingdom is "own" whichever faction it is. The player's in a soldier's run.
 */
export function seatFaction(state: RunState): FactionId {
  return playerSeat(state).faction;
}

/** One kind of command, narrowed out of the union. */
type CommandOf<Kind extends Command["kind"]> = Extract<Command, { readonly kind: Kind }>;

/**
 * What the door knows of one verb. `court`: the court's name for it, and `site`: the hex the court
 * judges it at (both absent for the free verbs the court never judges). `apply`: the rule it calls
 * for any seat but a city-command King. `city`: that King's meaning of it (absent: not one of his
 * verbs), `cityAllows` narrowing which of its commands he keeps.
 */
interface Verb<Kind extends Command["kind"]> {
  readonly court?: (command: CommandOf<Kind>) => CommandKind;
  readonly site?: (state: RunState, seat: Seat, command: CommandOf<Kind>) => AxialCoord | undefined;
  readonly apply: (state: RunState, seat: Seat, command: CommandOf<Kind>) => RunState;
  readonly city?: (state: RunState, faction: FactionId, command: CommandOf<Kind>) => RunState;
  readonly cityAllows?: (command: CommandOf<Kind>) => boolean;
}

/** The court judges a unit's command where the unit stands; undefined for a unit that is gone. */
const atUnit = (state: RunState, _seat: Seat, command: { readonly unitId: number }) =>
  unitById(state, command.unitId)?.coord;
const atCity = (_state: RunState, _seat: Seat, command: { readonly city: AxialCoord }) =>
  command.city;
const atHex = (_state: RunState, _seat: Seat, command: { readonly at: AxialCoord }) => command.at;
/** The throne's verbs are judged on the King's own hex. */
const atKing = (state: RunState, seat: Seat) => kingOf(state, seat.faction)?.coord;

/**
 * Every verb, one entry each: a new `Command` kind is one union member and one entry here (the
 * mapped type makes the table exhaustive). The rules behind the entries are not rewritten, so a
 * command through the door and the direct call give the same bytes (`test/command.test.ts`).
 */
const VERBS: { readonly [Kind in Command["kind"]]: Verb<Kind> } = {
  order: {
    court: () => "order",
    site: atUnit,
    apply: (state, seat, command) =>
      orderUnit(state, seat.faction, command.unitId, command.to, seat.role),
    city: (state, faction, command) => handOrder(state, faction, command.unitId, command.to),
  },
  stop: {
    court: () => "stop",
    site: atUnit,
    apply: (state, seat, command) =>
      cancelUnitOrder(state, seat.faction, command.unitId, seat.role),
    city: (state, faction, command) => releaseUnit(state, faction, command.unitId),
  },
  train: {
    court: () => "train",
    site: atCity,
    apply: (state, seat, command) =>
      buildUnit(state, seat.faction, command.city, command.unit, seat.role),
    city: (state, faction, command) => trainCityKind(state, faction, command.city, command.unit),
  },
  build: {
    court: (command) => buildCommand(command.structure),
    site: atHex,
    apply: (state, seat, command) =>
      buildStructure(state, seat.faction, command.at, command.structure, seat.role),
    // A city-command King founds in person; he builds nothing else.
    city: (state, faction, command) => sendToFound(state, faction, command.at),
    cityAllows: (command) => command.structure === "city",
  },
  raze: {
    court: () => "raze",
    site: atHex,
    apply: (state, seat, command) => razeHex(state, seat.faction, command.at),
  },
  govern: {
    court: () => "appoint",
    site: atUnit,
    apply: (state, seat, command) =>
      appointGovernor(state, seat.faction, command.unitId, command.city),
  },
  dismiss: {
    court: () => "dismiss",
    site: atCity,
    apply: (state, seat, command) => dismissMayor(state, seat.faction, command.city),
  },
  promote: {
    court: () => "promote",
    site: atUnit,
    apply: (state, seat, command) => promoteCommander(state, seat.faction, command.unitId),
  },
  demote: {
    court: () => "demote",
    site: atUnit,
    apply: (state, seat, command) => demoteCommander(state, seat.faction, command.unitId),
  },
  aim: {
    court: () => "aim",
    site: atUnit,
    apply: (state, seat, command) => aimCommander(state, seat.faction, command.unitId, command.at),
  },
  stance: {
    court: () => "stance",
    site: atCity,
    apply: (state, seat, command) => setStance(state, seat.faction, command.city, command.stance),
    // Garrison is a city's Auto and save its Pause.
    city: (state, faction, command) =>
      setCityMakes(state, faction, command.city, command.stance === "save" ? "pause" : undefined),
    cityAllows: (command) => command.stance === "garrison" || command.stance === "save",
  },
  mark: {
    court: () => "mark",
    site: atHex,
    apply: (state, seat, command) => setMark(state, seat.faction, command.at, command.from),
    // A banner from a city points that city's arrow; without one every city's.
    city: (state, faction, command) =>
      command.from
        ? pointCity(state, faction, command.from, command.at)
        : pointCities(state, faction, command.at),
  },
  clearMark: {
    apply: (state, seat, command) => clearMark(state, seat.faction, command.at),
    // Taking a city's banner down sends it Home.
    city: (state, faction, command) => pointCity(state, faction, command.at, command.at),
  },
  call: {
    court: () => "call",
    site: atKing,
    apply: (state, seat) => setCall(state, seat.faction),
  },
  clearCall: {
    apply: (state, seat) => clearCall(state, seat.faction),
  },
  tax: {
    court: () => "tax",
    site: atKing,
    apply: (state, seat) => raiseTax(state, seat.faction),
  },
  rush: {
    court: () => "rush",
    site: atCity,
    apply: (state, seat, command) => rushCity(state, seat.faction, command.city),
  },
  moveKing: {
    apply: (state, seat, command) => moveKing(state, seat.faction, command.to),
    city: (state, faction, command) => moveKing(state, faction, command.to),
  },
  stopKing: {
    apply: (state, seat) => stopKing(state, seat.faction),
    city: (state, faction) => stopKing(state, faction),
  },
  sell: {
    apply: (state, seat, command) => sellLoad(state, seat.faction, command.unitId, seat.role),
  },
  enlist: {
    apply: (state, seat, command) =>
      enlistWith(state, seat.faction, command.unitId, command.commanderId, seat.role),
  },
  command: {
    apply: (state, seat, command) => takeCommand(state, seat.faction, command.unitId, seat.role),
  },
  standDown: {
    apply: (state, seat, command) => standDown(state, seat.faction, command.unitId, seat.role),
  },
  // K1: a verb of a seat of the table, which only the door by number knows (`applyCommand`).
  deploy: {
    apply: (state) => state,
  },
};

/** The table's entry for a command. The one cast of the door: the table is keyed by the kind it handles. */
function verbOf(command: Command): Verb<Command["kind"]> {
  return VERBS[command.kind] as Verb<Command["kind"]>;
}

/** The court's name for a command; undefined for the free verbs the court never judges. */
export function commandVerb(command: Command): CommandKind | undefined {
  return verbOf(command).court?.(command);
}

/** The hex the court judges a seat's command at: the King's own hex for the throne's verbs; undefined for the free verbs or a unit that is gone. */
export function commandSite(state: RunState, seat: Seat, command: Command): AxialCoord | undefined {
  return verbOf(command).site?.(state, seat, command);
}

/** A unit the seat may order: its own, its squad's, or one standing on its land. */
function factionUnit(state: RunState, seat: Seat, unitId: number): Unit | undefined {
  const unit = unitById(state, unitId);
  return unit && unit.owner === seat.faction ? unit : undefined;
}

/** A mayor's land: the hex is nearest this seat's city among the faction's mayored cities. */
function onSeatsLand(state: RunState, seat: Seat, coord: AxialCoord): boolean {
  const city = seat.city && mayorOf(state, seat.faction, coord);
  return city !== undefined && seat.city !== undefined && hexEquals(city.coord, seat.city);
}

/**
 * Whether the seat has any say over the command. A King has it over everything of his
 * faction (the court judges the rest); a city-command King over his eight verbs only (CC1). A
 * soldier orders and stops its own unit, sells what it carries (M2), falls in with a
 * commander (C2), and buys Command and stands down (VC). A commander
 * does the same for itself and its squad. A mayor trains in its city, orders and stops a free
 * soldier (never a commander or a squad member) standing on its land, and buys improvements
 * there: exactly what `mayorTurn` does as its hands.
 */
export function seatAllows(state: RunState, seat: Seat, command: Command): boolean {
  switch (seat.role) {
    case "king":
      return !isCityCommand(state, seat.faction) || cityCommandAllows(command);
    case "soldier":
      return (
        (command.kind === "order" ||
          command.kind === "stop" ||
          command.kind === "sell" ||
          command.kind === "enlist" ||
          command.kind === "command" ||
          command.kind === "standDown") &&
        command.unitId === seat.unitId &&
        factionUnit(state, seat, command.unitId) !== undefined
      );
    case "commander": {
      if (command.kind !== "order" && command.kind !== "stop") {
        return false;
      }
      const unit = factionUnit(state, seat, command.unitId);
      return unit !== undefined && (unit.id === seat.unitId || unit.squadOf === seat.unitId);
    }
    case "mayor": {
      if (seat.city === undefined) {
        return false;
      }
      switch (command.kind) {
        case "train":
          return hexEquals(command.city, seat.city);
        case "build":
          return isImprovement(command.structure) && onSeatsLand(state, seat, command.at);
        case "order":
        case "stop": {
          const unit = factionUnit(state, seat, command.unitId);
          return (
            unit !== undefined &&
            !unit.commander &&
            unit.squadOf === undefined &&
            onSeatsLand(state, seat, unit.coord)
          );
        }
        default:
          return false;
      }
    }
    default:
      return false;
  }
}

/**
 * Why the door would refuse the command: the seat first, then the court's reach or Will for a
 * King. Undefined when the door lets it through; the rule itself may still find nothing to do
 * (no road, no such city) and hand the same state back.
 */
export function refusalOf(state: RunState, seat: Seat, command: Command): Refusal | undefined {
  if (!seatAllows(state, seat, command)) {
    return "seat";
  }
  const verb = commandVerb(command);
  const site = commandSite(state, seat, command);
  if (verb === undefined || site === undefined) {
    return undefined;
  }
  return commandRefusal(state, seat.faction, site, verb, seat.role);
}

/**
 * The door (K0): the command of the seat with this number, which the room stamps on it (ADR-0017
 * point 2), applied through the rule for its verb; `deploy` (K1) is the seat's own, by its number.
 * Same object back when refused or when no such seat exists.
 */
export function applyCommand(state: RunState, seatId: SeatId, command: Command): RunState {
  const seat = seatOf(state, seatId);
  if (seat && command.kind === "deploy") {
    return deploySeat(state, seatId, command.city);
  }
  return seat ? applySeatCommand(state, seat, command) : state;
}

/**
 * The door for a seat given as a value: a mayor's or a commander's, which hold no seat of the
 * table yet (C3), and the screen's questions about a command it has not sent (`refusalReason`).
 * Same object back when refused.
 */
export function applySeatCommand(state: RunState, seat: Seat, command: Command): RunState {
  if (!seatAllows(state, seat, command)) {
    return state;
  }
  const verb = verbOf(command);
  if (seat.role === "king" && isCityCommand(state, seat.faction)) {
    return verb.city?.(state, seat.faction, command) ?? state;
  }
  return verb.apply(state, seat, command);
}

/**
 * The verbs a city-command King keeps (CC1), each with its new meaning (`city` in `VERBS`):
 * mark and clearMark point a city, train and the stances garrison (Auto) and save (Pause) set
 * its kind, build founds a city in person, order and stop are his hand, and he rides with
 * moveKing and stopKing. The city script (CC4) passes this door from inside the tick.
 */
function cityCommandAllows(command: Command): boolean {
  const verb = verbOf(command);
  return verb.city !== undefined && (verb.cityAllows?.(command) ?? true);
}
